import {observer} from "mobx-react";
import ReactDOM from "react-dom";
import {useEffect, useRef, useState} from "react";
import {Magic8BallAnimation} from "./magic-8-ball-animation";
import {CloseIcon} from "../../themes/defaults/icons/close.icon";
import {Magic8BallOverlayBox} from "./boxes/magic-8-ball-overlay.box";
import {Magic8BallOverlayCloseBox} from "./boxes/magic-8-ball-overlay-close.box";
import {Magic8BallDisclaimerBox} from "./boxes/magic-8-ball-disclaimer.box";
import {Magic8BallMobileLandscapeWarningBox} from "./boxes/magic-8-ball-mobile-landscape-warning.box";
import {clamp} from "../../../common/utils/math-util";
import {Link} from "../raw-components/link/link";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {withServices} from "../../react-hooks/with-services";
import classNames from "classnames";
import {CyClass} from "../../../common/cy-classes";
import {Magic8BallPositionContextBox} from "./boxes/magic-8-ball-position-context.box";
import {MAGIC8BALL_OPENED_FROM} from "../../services/magic-8-ball/magic-8-ball-opened-from.enum";


interface IMagic8BallComponentProps extends ComponentServicesPick<'mediaQuery' | 'gsap' | 'theme' | 'translation' | 'pixiApp' | 'magic8ball' | 'inGameTicket'| 'inGameChallenge' | 'gameRunner'> {
    className?: string;
    showIntent: boolean;
}

const ComponentBody: React.FC<IMagic8BallComponentProps> = observer((props) => {
    let magic8BallContainerElementRef = useRef<HTMLDivElement>(null);
    let magic8BallAnimation = useRef<null | Magic8BallAnimation>(null);
    let [isShown, setIsShown] = useState(false);
    let [sizeScale, setSizeScale] = useState(1);

    useEffect(()=>{
        props.inGameChallenge.sendAnalytics('Show', 'MiniGameRender');
    }, [props.inGameChallenge])

    useEffect(() => {
        const _getOpenedFrom = () => {
            if (props.gameRunner.currentRunningGame) {
                return MAGIC8BALL_OPENED_FROM.GAME;
            } else {
                return MAGIC8BALL_OPENED_FROM.MENU;
            }
        }
        
        if (props.showIntent) {
            if (props.mediaQuery.mAndBelow && props.mediaQuery.landscape) {
                // we must show the layer so the user can see the rotate device message
                setIsShown(true);
            } else if (magic8BallContainerElementRef.current) {
                setIsShown(false);
                const services = {
                    mediaQuery: props.mediaQuery,
                    gsap: props.gsap,
                    theme: props.theme,
                    translation: props.translation,
                    pixiApp: props.pixiApp,
                    magic8ball: props.magic8ball,
                    inGameTicket: props.inGameTicket,
                    inGameChallenge: props.inGameChallenge
                };

                let magic8BallAnim = new Magic8BallAnimation(
                    services,
                    _getOpenedFrom(),
                    (newScale) => {
                        setSizeScale(newScale);
                    }
                );
                const initPromise = magic8BallAnim.init();
            
                initPromise.then(() => {
                    magic8BallAnim.attachToDOM(magic8BallContainerElementRef.current)
                    magic8BallAnimation.current = magic8BallAnim;
                    setIsShown(true);
                }).catch((e) => {
                    props.magic8ball.onError(props.translation.translate("MAGIC_8_BALL_GET_PRIZES_ERROR"), e);
                });
                
            }
        }
        if (!props.showIntent) {
            setIsShown(false);
        }
        return () => {
            if (magic8BallAnimation.current) {
                magic8BallAnimation.current.dispose();
                magic8BallAnimation.current = null
            }
        }
    }, [
        props.showIntent,
        props.gameRunner.currentRunningGame,
        // services
        props.mediaQuery, props.gsap, props.theme, props.translation, props.pixiApp, props.magic8ball, props.inGameTicket, props.inGameChallenge,
        // media queries
        props.mediaQuery.mAndBelow, props.mediaQuery.landscape
    ]);

    return ReactDOM.createPortal(<Magic8BallOverlayBox isShown={isShown}>
        <Magic8BallPositionContextBox>
            <Magic8BallOverlayCloseBox
                className={classNames(CyClass("cy-magic8ball-minigame-X-close-button"))}
                onClick={() => {
                    props.magic8ball.hideMagicBall();
                    props.inGameChallenge.sendAnalytics('Click', 'IGCXButton');
                }}
            >
                <CloseIcon size={3} />
            </Magic8BallOverlayCloseBox>
            <div ref={magic8BallContainerElementRef}>
                {isShown && props.mediaQuery.mAndBelow && props.mediaQuery.landscape ? <Magic8BallMobileLandscapeWarningBox>
                    <div>
                        <img
                            src={props.theme.currentTheme.assets.images.rotate_phone_landscape_png.toString()}
                            style={{display: "block", margin: "0 auto 1rem"}}
                            alt="rotate phone from landscape to portrait"
                        />
                    </div>
                    <div>{props.translation.translate("ROTATE_DEVICE_TO_PORTRAIT")}</div>
                </Magic8BallMobileLandscapeWarningBox> : null}
            </div>
            {props.showIntent && props.mediaQuery.mAndBelow && props.mediaQuery.portrait ? <Magic8BallDisclaimerBox style={{fontSize: clamp(1.4 * sizeScale, 1, 1.4) + "rem"}}>
                Deposit min €10 for one daily spin. Existing players with min €10 lifetime deposit can spin one a day.
                Prizes must be claimed within 24 hours and vary between Cash, Free Spins and Bonus. Odds differ between prizes. no guaranteed win.
                <Link href="?">Full T&C apply</Link>
            </Magic8BallDisclaimerBox> : null}
        </Magic8BallPositionContextBox>
    </Magic8BallOverlayBox>, document.body);
});

export const Magic8BallAnimationComponent = withServices(ComponentBody, ['mediaQuery', 'gsap', 'theme', 'translation', 'pixiApp', 'magic8ball', 'inGameTicket', 'inGameChallenge', 'gameRunner']);
